/**
 * Active profile.
 *
 * A "profile" is a person whose medication is tracked; a user always has one
 * self-profile and may have access to others (a child, a parent) through
 * grants. Almost every screen is scoped to one, so the choice lives here.
 *
 * Three rules, and the third is a security property rather than a nicety:
 *
 * 1. The choice persists to localStorage, so opening the app on a phone does
 *    not land a caregiver on their own profile when they were looking after
 *    someone else.
 * 2. If nothing is stored, fall back to the user's own self-profile.
 * 3. THE STORED ID IS RE-VALIDATED AGAINST THE LIVE PROFILE LIST ON EVERY LOAD.
 *    A grant can be revoked while a tab sits open or between sessions. If the
 *    stored id is no longer in the list the app must not keep pointing at it:
 *    every request would 404 (the API returns 404, not 403, for a profile you
 *    cannot see) and the UI would show a permanently broken screen for a
 *    profile the user was deliberately removed from. So a stale id is dropped
 *    and replaced with the self-profile.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Profile } from '@medtrack/shared';

import { api, ApiClientError } from './api';
import { qk } from './queryKeys';
import { useSession } from './session';

export const ACTIVE_PROFILE_STORAGE_KEY = 'medtrack-active-profile';

export interface ProfileContextValue {
  /** The profile every scoped screen should read. Null while loading, or if the user has none. */
  profile: Profile | null;
  /** Every profile this user can see. Empty while loading. */
  profiles: Profile[];
  setProfileId: (profileId: string) => void;
  isLoading: boolean;
  /** Non-null when the profile list itself failed to load. Screens must show <ErrorState>. */
  error: ApiClientError | null;
  refetch: () => void;
}

const ProfileContext = createContext<ProfileContextValue | null>(null);
ProfileContext.displayName = 'ProfileContext';

export { ProfileContext };

function readStoredProfileId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(ACTIVE_PROFILE_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredProfileId(profileId: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (profileId === null) window.localStorage.removeItem(ACTIVE_PROFILE_STORAGE_KEY);
    else window.localStorage.setItem(ACTIVE_PROFILE_STORAGE_KEY, profileId);
  } catch {
    /* Storage unavailable; the choice just does not survive a reload. */
  }
}

/**
 * Pick the profile to show. Exported for testing and because the precedence is
 * worth being able to read in one place:
 *   stored id, if it is still in the list -> self-profile -> first profile.
 */
export function resolveActiveProfile(
  profiles: readonly Profile[],
  storedId: string | null,
): Profile | null {
  if (profiles.length === 0) return null;

  if (storedId) {
    const stored = profiles.find((p) => p.id === storedId);
    // Deliberately does not fall through to "id is unknown, keep it anyway".
    if (stored) return stored;
  }

  const self = profiles.find((p) => p.isSelf);
  if (self) return self;

  return profiles[0] ?? null;
}

export function ProfileProvider({ children }: { children: ReactNode }): JSX.Element {
  const session = useSession();
  const sessionProfiles = session.data?.profiles;

  /**
   * `/api/profiles` is the authority on the list. The session payload carries
   * the same array, so it seeds this query as placeholder data and there is no
   * empty flash on boot — but a revoked grant shows up here on the next fetch
   * even if the cached session is older.
   */
  const profilesQuery = useQuery<Profile[], ApiClientError>({
    queryKey: qk.profiles(),
    queryFn: ({ signal }) => api.get<Profile[]>('/profiles', undefined, { signal }),
    enabled: session.isSuccess,
    placeholderData: sessionProfiles,
    staleTime: 60 * 1000,
  });

  const profiles = useMemo<Profile[]>(
    () => profilesQuery.data ?? sessionProfiles ?? [],
    [profilesQuery.data, sessionProfiles],
  );

  const [storedId, setStoredId] = useState<string | null>(() => readStoredProfileId());

  const active = useMemo(() => resolveActiveProfile(profiles, storedId), [profiles, storedId]);

  /**
   * Re-validation, rule 3. When the resolved profile is not the stored one —
   * because the stored id vanished from the list, or nothing was stored — write
   * the corrected id back so the next boot starts from a valid choice.
   */
  useEffect(() => {
    if (profiles.length === 0) return;
    if (!active) return;
    if (storedId === active.id) return;
    writeStoredProfileId(active.id);
    setStoredId(active.id);
  }, [active, profiles.length, storedId]);

  const setProfileId = useCallback(
    (profileId: string) => {
      // Refuse an id the user has no grant on rather than persisting it and
      // letting every subsequent request 404.
      const exists = profiles.some((p) => p.id === profileId);
      if (!exists) return;
      writeStoredProfileId(profileId);
      setStoredId(profileId);
    },
    [profiles],
  );

  const refetch = useCallback(() => {
    void profilesQuery.refetch();
  }, [profilesQuery]);

  const value = useMemo<ProfileContextValue>(
    () => ({
      profile: active,
      profiles,
      setProfileId,
      // Only a genuine first load counts as loading; a background refetch with
      // data already on screen must not blank the page.
      isLoading: (session.isLoading || profilesQuery.isLoading) && profiles.length === 0,
      error: profilesQuery.error ?? null,
      refetch,
    }),
    [
      active,
      profiles,
      setProfileId,
      session.isLoading,
      profilesQuery.isLoading,
      profilesQuery.error,
      refetch,
    ],
  );

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
}

/**
 * The active profile and the list to switch between.
 *
 * Throws if used outside <ProfileProvider>, which is mounted inside
 * <RequireAuth> — a screen that renders without a session has no profile to
 * talk about, and silently returning null would turn that into a confusing
 * empty state instead of an obvious wiring bug.
 */
export function useActiveProfile(): ProfileContextValue {
  const ctx = useContext(ProfileContext);
  if (!ctx) {
    throw new Error('useActiveProfile must be used inside <ProfileProvider>');
  }
  return ctx;
}

/**
 * Convenience for the common case: the id, or null while it resolves.
 * Guard your queries with `enabled: !!profileId` rather than passing ''.
 */
export function useActiveProfileId(): string | null {
  return useActiveProfile().profile?.id ?? null;
}

/** Role helpers. `viewer < caregiver < owner`. */
const ROLE_RANK = { viewer: 0, caregiver: 1, owner: 2 } as const;

export function useCanEdit(): boolean {
  const { profile } = useActiveProfile();
  if (!profile) return false;
  return ROLE_RANK[profile.role] >= ROLE_RANK.caregiver;
}

export function useIsOwner(): boolean {
  const { profile } = useActiveProfile();
  if (!profile) return false;
  return profile.role === 'owner';
}
